/**
 * AddonRecommendations — booking-funnel "Pairs well with…" card.
 *
 * When a client picks a service in the booking funnel, this card surfaces
 * 1-3 add-ons that pair well with that treatment (sourced from each
 * add-on's `compatible_with` list in the DB). Each option has a single-
 * tap Add / Remove toggle so they can stack a Hydrafacial with LED +
 * Lymphatic Massage in two taps.
 *
 * Selections flow up via `onChange(selectedAddonsArray)` so the parent
 * funnel can append them to the booking notes + price total.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Plus, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AddonRecommendations({ service, selected = [], onChange }) {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!service) { setRecs([]); return; }
    setLoading(true);
    axios.get(`${API}/api/addons/recommended`, { params: { service, limit: 3 }, timeout: 8000 })
      .then((r) => setRecs(r.data?.addons || []))
      .catch(() => setRecs([]))
      .finally(() => setLoading(false));
  }, [service]);

  if (loading || recs.length === 0) return null;

  const isPicked = (slug) => selected?.some?.((a) => a.slug === slug);
  const toggle = (a) => {
    if (!onChange) return;
    if (isPicked(a.slug)) {
      onChange(selected.filter((s) => s.slug !== a.slug));
    } else {
      onChange([...selected, { slug: a.slug, name: a.name, price_from: a.price_from, duration_min: a.duration_min }]);
    }
  };

  return (
    <div
      className="rounded-2xl border border-amber-400/60 bg-gradient-to-br from-amber-50 via-white to-white p-4 my-4"
      data-testid="addon-recommendations"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-amber-600" />
        <h3 className="text-sm font-bold text-amber-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          Karine recommends — pairs well with this treatment
        </h3>
      </div>
      <p className="text-[11px] text-amber-800 mb-3 leading-snug">
        Stack these onto your session for a noticeably better result. Tap to add — billed at checkout, not now.
      </p>
      <div className="space-y-2">
        {recs.map((a) => {
          const picked = isPicked(a.slug);
          return (
            <button
              type="button"
              key={a.slug}
              onClick={() => toggle(a)}
              className={`w-full flex items-start gap-3 px-3 py-2.5 min-h-[52px] rounded-lg border-2 transition-colors text-left ${
                picked ? "border-amber-600 bg-amber-100/70" : "border-amber-200 bg-white hover:border-amber-300"
              }`}
              data-testid={`addon-rec-${a.slug}`}
            >
              <span className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${picked ? "border-amber-600 bg-amber-600" : "border-amber-400"}`}>
                {picked ? <Check className="w-3.5 h-3.5 text-white" /> : <Plus className="w-3.5 h-3.5 text-amber-500" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-sm font-bold text-amber-900 block leading-tight">{a.name}</span>
                {a.subtitle && (
                  <span className="text-[11px] text-amber-700 block">{a.subtitle}</span>
                )}
                <span className="text-[11px] text-stone-600 line-clamp-2 leading-snug mt-0.5">{a.description}</span>
              </span>
              <span className="text-sm font-bold text-emerald-700 tabular-nums whitespace-nowrap flex-shrink-0">
                +${a.price_from}
                {a.price_to && a.price_to !== a.price_from ? `–${a.price_to}` : ""}
              </span>
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-900">
          ✨ <b>{selected.length} add-on{selected.length === 1 ? "" : "s"}</b> queued for your visit:&nbsp;
          {selected.map((s) => s.name).join(" · ")}.
        </div>
      )}
    </div>
  );
}
